import { useEffect, useMemo, useState } from 'react'
import { DataGrid } from '@/presentation/common/components/dataGrid/DataGrid.tsx'
import {
  DataGridApiConfig,
  DataGridColumnDefs,
  DataGridDefaultColDef,
  RowNode,
} from '@/presentation/common/components/dataGrid/lib/api.types.ts'
import { MTCellUidV2 } from '@/presentation/common/components/maintainer/table/cells/MTCellUidV2.tsx'
import { stringComparator } from '@/presentation/common/components/table/comparators/stringComparator.ts'
import { useMQ } from '@/presentation/common/hooks/useMediaQuery.ts'
import { useMaintainerStore } from '@/presentation/common/store/zustand/maintainer.ts'
import { ProductsTemplatesActions } from '@/presentation/productsTemplates/forms/ProductsTemplatesActions.tsx'
import {
  useQueryProductsTemplatesForTable,
} from '@/presentation/productsTemplates/hooks/useQueryProductsTemplatesForTable.ts'
import {
  useProductsTemplatesMaintainerStore,
} from '@/presentation/productsTemplates/store/productsTemplatesMaintainers.ts'

export const ProductsTemplatesTable = () => {
  const { isTabletAndBelow } = useMQ()
  const { selectedTab, setSelectedTemplateId } = useProductsTemplatesMaintainerStore()
  const { reset, setTitle, setSelectionV2, setActionsSlot, search } = useMaintainerStore()
  const { data, query, refresh, isLoading } = useQueryProductsTemplatesForTable()

  // ayuda a mantener la selección cuando se cambia de tab, cuando el tab vuelva a ser maintainers, se configura la selección del mantenedor con esta variable
  const [ currentSelection, setCurrentSelection ] = useState<RowNode[]>([])

  const defaultColDef: DataGridDefaultColDef = useMemo(() => ({
    sortable  : true,
    resizable : true,
    minWidth  : 100,
  }), [])

  const columnDefs: DataGridColumnDefs[] = useMemo(() => ([
    {
      field                   : 'description',
      headerName              : 'Descripción',
      headerCheckboxSelection : true,
      checkboxSelection       : true,
      comparator              : stringComparator,
    },
    {
      field        : 'id',
      headerName   : 'ID',
      cellRenderer : MTCellUidV2,
      comparator   : stringComparator,
    },
  ]), [])

  const tableConfig = useMemo(() => ({
    useMobileView : isTabletAndBelow,
    defaultColDef,
    columnDefs,
    rowHeight     : 50,
    rowData       : data?.items || [],
    rowSelection  : 'multiple',
    pagination    : {
      enabled      : true,
      pageSize     : data?.limit || 10,
      currentPage  : data?.page || 1,
      totalItems   : data?.totalItems || 0,
      onPageChange : (page: number, limit: number) => query({
        params: {
          page,
          limit,
          search,
        },
      }),
    },
    onSelectionChange: (_selectedRow, selectedRows) => {
      if (selectedTab === 'templates') {
        setCurrentSelection(selectedRows)
        setSelectionV2(selectedRows)
        setSelectedTemplateId(selectedRows.length === 1 ? selectedRows[0]?.data.id : null)
      }
    },
  } as DataGridApiConfig), [ data, data?.items, data?.limit, data?.page, data?.totalItems, isTabletAndBelow ])

  const actionsComponent = useMemo(() => <ProductsTemplatesActions onAction={refresh}/>, [ refresh ])

  // actualizar actions slot
  useEffect(() => {
    if (selectedTab === 'templates') {
      setActionsSlot(actionsComponent)
    }
  }, [ actionsComponent, selectedTab ])

  // first load
  useEffect(() => {
    query({
      params: {
        page: 1,
      },
    })
  }, [ ])

  // cuando cambia el tab
  useEffect(() => {
    if (selectedTab === 'templates') {
      reset()
      setTitle('Plantillas de productos')
      setActionsSlot(actionsComponent)
      setSelectionV2(currentSelection)
    }
  }, [ selectedTab ])

  // when search changes update query
  useEffect(() => {
    if (selectedTab === 'templates') {
      query({
        params: {
          search,
          page: 1,
        },
      })
    }
  }, [ search ])

  return (
    <div style={{
      display: selectedTab === 'templates' ? 'block' : 'none',
    }}>
      <DataGrid config={tableConfig} isLoading={isLoading}/>
    </div>
  )
}
